import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Role, UserState } from 'src/app/State/UserState';
import { environment } from 'src/environments/environment';
import { UserProfile } from './UserProfile';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  public user$: BehaviorSubject<UserState> = new BehaviorSubject<UserState>({
    user: null,
    role: null,
  });
  public role$: BehaviorSubject<Role> = new BehaviorSubject<Role>(null);

  private user: UserProfile | null;
  static imgReload = new EventEmitter<void>();

  constructor(private http: HttpClient) {}

  public userProfileObservable(): Observable<UserProfile | null> {
    if (!this.user) {
      return this.http.get<UserProfile>(`${environment.apiUrl}/user`).pipe(
        catchError(() => of(null)),
        tap((user: UserProfile | null) => this.userChanged(user))
      );
    }

    return of(this.user);
  }

  /**
   * @deprecated Use user$.subscribe or $role.subscribe instead
   */
  public async getUserProfile(forceReload: boolean = false): Promise<any> {
    if (!this.user || forceReload) {
      const user = await this.http
        .get<UserProfile>(`${environment.apiUrl}/user`)
        .toPromise();

      this.userChanged(user);
    }

    return this.user;
  }

  public getUploadImageUrl() {
    return `${environment.apiUrl}/profile/save-image`;
  }

  public getRemoveImageUrl() {
    return `${environment.apiUrl}/profile/remove-image`;
  }

  public changePassword(params) {
    return new Promise((resolve) => {
      let url = `${environment.apiUrl}/profile/change-password`;

      this.http.post(url, params).subscribe(
        (res) => resolve(res),
        (err) => {
          console.error(err);
          resolve({
            success: false,
            msg: err.error.message,
          });
        }
      );
    });
  }

  uploadDocument(fileType: string, file: File) {
    let formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);

    return new Promise((resolve) => {
      let url = `${environment.apiUrl}/document/profile/upload`;

      this.http.post(url, formData).subscribe(
        (res) => resolve(res),
        (err) => {
          console.error(err);
          resolve({
            success: false,
            msg: err.error.message,
          });
        }
      );
    });
  }

  public downloadDocument(fileId: number) {
    return new Promise((resolve) => {
      let url = `${environment.apiUrl}/document/download/${fileId}`;
      this.http.get(url).subscribe(
        (res) => resolve(res),
        (err) => {
          console.error(err);
          resolve({
            success: false,
            msg: err.error.message,
          });
        }
      );
    });
  }

  reUploadDocument(fileType: string, fileId: number, file: File) {
    let formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);

    return new Promise((resolve) => {
      let url = `${environment.apiUrl}/document/profile/upload/${fileId}`;

      this.http.post(url, formData).subscribe(
        (res) => resolve(res),
        (err) => {
          console.error(err);
          resolve({
            success: false,
            msg: err.error.message,
          });
        }
      );
    });
  }

  public async update(params): Promise<unknown> {
    const url = `${environment.apiUrl}/profile`;

    try {
      const response = await this.http.put(url, params).toPromise();

      this.getUserProfile(true);

      return response;
    } catch (err) {
      console.error(err);

      return {
        success: false,
        msg: err.error.message,
      };
    }
  }

  public async updateMfaPreference(mfaEnabled: boolean): Promise<unknown> {
    const url = `${environment.apiUrl}/profile/mfa`;

    try {
      const response = await this.http
        .patch(url, { mfa_enabled: mfaEnabled })
        .toPromise();

      if (this.user) {
        this.user.mfa_enabled = mfaEnabled;
        this.userChanged(this.user);
      }

      return response;
    } catch (err) {
      console.error(err);

      return {
        success: false,
        msg: err.error?.message || 'Unable to update MFA preference',
      };
    }
  }

  public loadImgProfileLink() {
    return `${environment.apiUrl}/profile/load-image-profile`;
  }

  private userChanged(user: UserProfile | null): void {
    if (user === null) {
      this.user = null;
      return;
    }

    const role: Role = {
      role: user.current_role,
      isAdmin: user.current_role === 'admin',
      isAccountant: user.current_role === 'accountant',
      isPartner: user.current_role === 'partner',
      isCustomer: user.current_role === 'customer',
      isAffiliate: user.current_role === 'affiliate',
    };

    this.user = user;

    this.user$.next({
      user: user,
      role: role,
    });

    this.role$.next(role);
  }
}
